/**
 * Workspace-only supervisor email: search members by name/email, then select (corporate directory UX).
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { listWorkspaceMembers } from '@/services/workspaceApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function norm(s) {
  return (s || '').trim().toLowerCase();
}

export default function SupervisorMemberPicker({ value, onChange, tenantId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const load = useCallback(async () => {
    const tid = (tenantId || '').trim();
    if (!tid) {
      setMembers([]);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const token = getRestApiBearerToken();
      if (!token) throw new Error('Not signed in');
      const data = await listWorkspaceMembers(API_BASE_URL, token, tid);
      setMembers(Array.isArray(data.members) ? data.members : []);
    } catch (e) {
      setLoadError(e.message || 'Could not load members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedMember = useMemo(() => {
    const v = norm(value);
    if (!v) return null;
    return members.find((m) => norm(m.email) === v) || null;
  }, [members, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = members.filter((m) => (m.status || 'ACTIVE').toUpperCase() !== 'REVOKED');
    if (!q) {
      return [...list]
        .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''))
        .slice(0, 40);
    }
    return list
      .filter((m) => {
        const name = (m.displayName || '').toLowerCase();
        const em = (m.email || '').toLowerCase();
        return name.includes(q) || em.includes(q);
      })
      .slice(0, 50);
  }, [members, query]);

  useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (m) => {
    const em = (m.email || '').trim();
    if (!em) return;
    onChange(em);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setOpen(false);
  };

  const tid = (tenantId || '').trim();
  if (!tid) {
    return (
      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        Choose an active workspace (tenant) to select a supervisor from the member directory.
      </p>
    );
  }

  return (
    <div ref={rootRef} className="space-y-2">
      <p className="text-xs text-gray-500">
        Only people who belong to this workspace can be supervisors. Search by name or email, then select one.
      </p>

      {loadError && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{loadError}</p>
      )}

      {value && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <span className="text-gray-800">
            {selectedMember ? (
              <>
                <span className="font-medium">{selectedMember.displayName || selectedMember.email}</span>
                <span className="text-gray-500"> · {selectedMember.email}</span>
                <span className="ml-2 text-xs text-gray-400">({selectedMember.role})</span>
              </>
            ) : (
              <>
                <span className="text-amber-800">Not in directory (legacy or removed):</span>{' '}
                <span className="font-mono text-xs">{value}</span>
              </>
            )}
          </span>
          <button
            type="button"
            onClick={clear}
            className="ml-auto text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            Clear
          </button>
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          autoComplete="off"
          placeholder={loading ? 'Loading members…' : 'Search workspace members by name or email…'}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={loading || !!loadError}
          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
        />
        {open && !loadError && (
          <ul
            className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            role="listbox"
          >
            {loading && (
              <li className="px-3 py-2 text-sm text-gray-500">Loading…</li>
            )}
            {!loading && members.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">No members found for this workspace.</li>
            )}
            {!loading && members.length > 0 && filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">No matching members. Try another search.</li>
            )}
            {!loading &&
              filtered.map((m) => (
              <li key={m.userSub || m.email}>
                <button
                  type="button"
                  className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-blue-50"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(m)}
                >
                  <span className="font-medium text-gray-900">{m.displayName || m.email || 'Member'}</span>
                  <span className="text-xs text-gray-500">{m.email}</span>
                  <span className="text-[11px] uppercase tracking-wide text-gray-400">{m.role}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
